import React from 'react';
import { useSelector } from 'react-redux';
import propTypes from 'prop-types';
import {
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Line,
  ReferenceDot
} from 'recharts';
import NewPlanBaseReferences from './NewPlanBaseReferences';
import RecommendationBaseReferences from './RecommendationBaseReferences';
import FieldAverageReferences from './FieldAverageReferences';

import { getMessage } from 'util/locale';
import { convert, inferUnit } from 'util/unitConversion';
import { convertScenarioForLocale } from 'advanced/scenarios/util/convertScenarioForLocale';
import { sortByArea } from 'util/sort';
import { formatNumberRoundDownToNearest, formatNumberRoundUpToNearest } from 'util/formatters/numberFormatter';
import COLORS from 'constants/colors';
import { LINE_TYPES, X_AXIS_STEPS, X_AXIS_TICKS } from 'constants/advanced';
import { UNIT_TYPES } from 'constants/unitTypes';

import { 
  getActiveScenario,
  getScriptCropNominalWeight,
  getFieldDensityCurve,
  getZonesDensityCurves, 
  getCurvesBounds
} from 'advanced/scenarios/scenarioSelectors';
import { getFVSSScenario } from 'advanced/scenarios/selectors';

const BaseGraph = ({ children, linesToShow }) => {
  const activeScenario = useSelector(getActiveScenario);
  const fvssScenario = useSelector(getFVSSScenario);
  const fieldCurve = useSelector(getFieldDensityCurve);
  const zonesCurves = useSelector(getZonesDensityCurves);
  const cropNominalWeight = useSelector(getScriptCropNominalWeight);
  const usersSeedDensityUnit = inferUnit('seed-density').unitKey;
  const [lowerBound, upperBound] = useSelector(getCurvesBounds).map(bound => convert({
    from: { u: UNIT_TYPES.seed_ac, q: bound },
    to: { u: usersSeedDensityUnit }
  }).q
  );
  const convertedActiveScenario = convertScenarioForLocale({ scenario: activeScenario, cropNominalWeight });
  const convertedFvssScenario = convertScenarioForLocale({ scenario: fvssScenario, cropNominalWeight });
  const { zones } = convertedActiveScenario;

  const xAxisStep = X_AXIS_STEPS[usersSeedDensityUnit];
  const tickMin = formatNumberRoundDownToNearest(lowerBound, xAxisStep);
  const tickMax = formatNumberRoundDownToNearest(upperBound, xAxisStep);
  const ticks = Array.from(
    { length: ((tickMax - tickMin) / xAxisStep) + 1 },
    (_, index) => tickMin + index * formatNumberRoundDownToNearest(tickMax / X_AXIS_TICKS, xAxisStep)
  ).filter((tick =>
    tick <= formatNumberRoundUpToNearest(upperBound, xAxisStep)
    && tick >= tickMin
    && tick > xAxisStep
  ));

  // We'll asume that the field curve has the same target yield points as the curve of each zone
  // and that they are always organized in the same order.
  const graphData = fieldCurve.map((coordinate, index) => {
    let result = coordinate;
    Object.keys(zonesCurves).forEach(zoneNumber => {
      result = { ...result, ...zonesCurves[zoneNumber][index]};
    });
    return result;
  });

  const minYield = formatNumberRoundDownToNearest(Math.min(...graphData.flatMap((data) => Object.values(data))), 0.1);

  return (
    <section className='base-graph'>
      <p>{getMessage('estimated-yield')}{` (${inferUnit('area-density.yield').symbol.quantitySpecific})`}</p>
      <LineChart
        width={602}
        height={300}
        data={graphData}
        margin={{ left: -10 }}
      >
        <CartesianGrid horizontal={false} vertical={false} fill='white' />
        <XAxis
          dataKey='plantingRate'
          type='number'
          tickFormatter={(tick) => `${tick / 1000}`}
          padding={{ right: 20, left: 20 }}
          domain={[lowerBound, upperBound]}
          ticks={ticks}
        />
        <YAxis
          domain={['0', 'auto']}
          tickCount={5}
          padding={{ top: 20 }}
          includeHidden
        />
        {/* Field  Density Curve */}
        <Line 
          dataKey='fieldAverageYield'
          hide={linesToShow !== LINE_TYPES.FIELD_AVERAGE_YIELD}
          stroke={COLORS.scenarios.recommendation}
          type='monotone'
          strokeWidth={3}
          dot={false}
          isAnimationActive={false}
        />
        {Object.values(zones).sort(sortByArea).map((zone, index) => {
          const { rate, 'estimated-yield': estimatedYield } = zone.meta.applications.seed[0];

          return (
            <React.Fragment key={zone.meta.zoneId}>
              <Line
                dataKey={`zone-${index + 1}-yield`}
                hide={linesToShow !== LINE_TYPES.BY_ZONES_YIELD}
                stroke={COLORS.scenarios[`zone-${index + 1}`]}
                type='monotone'
                strokeWidth={2}
                dot={false}
                opacity={0.5}
              />
              {linesToShow === LINE_TYPES.BY_ZONES_YIELD 
              && <ReferenceDot
                r={2}
                x={rate.q}
                y={estimatedYield.q}
                fill={COLORS.scenarios[`zone-${index + 1}`]}
                stroke={COLORS.scenarios[`zone-${index + 1}`]}
              />}
            </React.Fragment>
          );
        }
        )}
        {children}
        {/* This way of calling a component is a workaround implemented because 
        Recharts does not allow to wrap its components with custom ones. 
        See: https://github.com/recharts/recharts/issues/412 */}
        {RecommendationBaseReferences()}
        {linesToShow === LINE_TYPES.FIELD_AVERAGE_YIELD && FieldAverageReferences({ 
          minYield,
          newPlan: { 
            plantingRate: convertedActiveScenario.avgRate.q,
            estimatedYield: convertedActiveScenario.avgEstYield.q 
          },
          fvss: { 
            plantingRate: convertedFvssScenario.avgRate.q,
            estimatedYield: convertedFvssScenario.avgEstYield.q
          }
        })}
        {NewPlanBaseReferences({ 
          plantingRate: convertedActiveScenario.avgRate.q,
          estimatedYield: convertedActiveScenario.avgEstYield.q,
          minYield
        })}
      </LineChart>
    </section>
  );
};
BaseGraph.propTypes = {
  children: propTypes.node,
  linesToShow: propTypes.oneOf([
    LINE_TYPES.FIELD_AVERAGE_YIELD,
    LINE_TYPES.BY_ZONES_YIELD
  ])
};

export default BaseGraph;

